import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import Breadcrumb from "../../../core/common/Breadcrumb/breadcrumb";
import { useAuth } from "../../../context/AuthContext";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  const { login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const from =
    typeof location.state?.from === "string" ? location.state.from : undefined;

  // ✅ Check if there's a pending payment
  const pendingPaymentId = sessionStorage.getItem("pendingPaymentId");
  const pendingCourseTitle = sessionStorage.getItem(
    "pendingPaymentCourseTitle"
  );

  const handleLogin = async () => {
    setError("");
    try {
      await login(email, password, from);

      // ✅ Check if user just registered (show confirmation first)
      const justRegisteredPaymentId = sessionStorage.getItem(
        "justRegisteredPaymentId"
      );
      const justRegisteredCourseTitle = sessionStorage.getItem(
        "justRegisteredCourseTitle"
      );

      if (justRegisteredPaymentId) {
        // Clear the registration flags
        sessionStorage.removeItem("justRegisteredPaymentId");
        sessionStorage.removeItem("justRegisteredCourseTitle");

        // Show enrollment confirmation modal
        // We'll create this modal next
        navigate("/enrollment-confirmation", {
          state: {
            courseTitle: justRegisteredCourseTitle,
          },
        });
        return;
      }

      // ✅ Check for pending payment (existing user login)
      if (pendingPaymentId) {
        console.log("Found pending payment:", pendingPaymentId);
        setProcessingPayment(true);

        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/payments/${pendingPaymentId}/link-to-current-student`,
            {
              method: "POST",
              credentials: "include",
            }
          );

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || "Failed to link payment");
          }

          console.log("✅ Payment linked successfully");

          sessionStorage.removeItem("pendingPaymentId");
          sessionStorage.removeItem("pendingPaymentCourseTitle");

          // Show enrollment confirmation
          navigate("/enrollment-confirmation", {
            state: {
              courseTitle: pendingCourseTitle,
            },
          });
        } catch (linkError: any) {
          console.error("Failed to link payment:", linkError);
          sessionStorage.removeItem("pendingPaymentId");
          sessionStorage.removeItem("pendingPaymentCourseTitle");
          setError(
            "Login successful, but failed to link payment. Please contact support."
          );
          setProcessingPayment(false);
        }
      }
    } catch (err) {
      setError("Invalid email or password. Please try again.");
      setProcessingPayment(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  return (
    <>
      <Breadcrumb title="Sign in" />
      <div className="flex items-center justify-center bg-gray-50 px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-5xl w-full">
          {/* Photo Card */}
          <div className="bg-white rounded-xl shadow-md p-8 flex flex-col items-center justify-center">
            <img
              src="/assets/img/auth/g2g-login.png"
              alt="Guideline2Germany"
              className="w-3/4 mb-6 rounded-lg shadow-sm hover:scale-105 transition duration-300"
            />
            <h3 className="text-xl font-semibold text-center text-gray-700">
              Welcome to Guideline2Germany
            </h3>
            <p className="text-sm text-gray-500 text-center mt-2">
              Your trusted pathway to Ausbildung & Study in Germany.
            </p>
          </div>

          {/* Login Form Card */}
          <div className="bg-white rounded-xl shadow-md p-8 space-y-6">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-[#009fe3]">
                Welcome Back!
              </h2>
              <p className="mt-2 text-gray-600">Sign in to your account</p>
            </div>

            {/* Styled Error Message */}
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-red-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700 font-medium">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {/* Email Input */}
              <div>
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={processingPayment}
                  className="w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#009fe3] transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                  style={{ fontSize: "16px" }}
                />
              </div>

              {/* Password Input with Eye Icon */}
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={processingPayment}
                  className="w-full px-4 py-3 pr-12 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#009fe3] transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                  style={{ fontSize: "16px" }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={processingPayment}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none transition-colors disabled:opacity-50"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <FontAwesomeIcon
                    icon={showPassword ? faEyeSlash : faEye}
                    className="w-5 h-5"
                  />
                </button>
              </div>

              {/* Sign In Button */}
              <button
                onClick={handleLogin}
                disabled={processingPayment}
                className="w-full bg-[#009fe3] hover:bg-[#007cb5] text-white font-bold py-3 rounded-md transition duration-300 shadow-md hover:shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {processingPayment ? "Linking Payment..." : "Sign In"}
              </button>
            </div>

            {/* Forgot Password Link */}
            <div className="text-center mt-4 text-sm text-gray-600">
              <Link
                to="/forgot-password"
                className="text-[#009fe3] hover:underline font-medium"
              >
                Forgot Password?
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
